import React, { useEffect, useState } from 'react';
import { useAppContext } from 'src/components/app-context';
import { useOfferListFilterContext } from 'src/areas/product/components/offer-list-filter-context';
import type { IListPrices } from '../price-list-panel/types';
import { createAlertRequest } from 'src/areas/sidebar/actions/alert';
import { StockStatusAlertModal } from './stock-status-alert-modal';
import { useIsAuthenticated } from 'src/hooks/use-is-authenticated';
import { useDispatch, useSelector } from 'react-redux';
import type { AppState } from 'src/reducers';
import type { ProductMainOffer } from 'src/types';
import {
  AlertTypes,
  Errors,
} from 'src/areas/product-alert/components/product-alert-form/types';
import { isValidEmail } from 'src/areas/account/helpers';
import { useHistory } from 'react-router-dom';
import { FilterType } from 'src/components/price-list/types';
import { useProductContext } from '../../product-context';
import type { PromptMessageType } from './prompt-message';
import { PromptMessage } from './prompt-message';
import type { InfoMessageType } from './info-message';
import { InfoMessage } from './info-message';
import { IconChevronRight } from '@prisjakt/ui/icon/chevron-right';
import { IconNotificationOutlined } from '@prisjakt/ui/icon/notification-outlined';

type OfferListEmptyMessageProps = {
  offers: IListPrices;
  allOffers: ProductMainOffer[];
};

enum PromptMessageEnum {
  outOfStock = 'outOfStock',
  noOffers = 'noOffers',
  noFeaturedSellsProduct = 'noFeaturedSellsProduct',
}

enum InfoMessageEnum {
  noFiltersMatch = 'noFiltersMatch',
  noInfoOnDelivery = 'noInfoOnDelivery',
  outOfStock = 'outOfStock',
}

const OfferListEmptyMessage = ({
  offers,
  allOffers,
}: OfferListEmptyMessageProps) => {
  const { t } = useAppContext();
  const dispatch = useDispatch();
  const isAuthenticated = useIsAuthenticated();
  const { filtersToggledOnByUser, resetFilters, toggleFilter } =
    useOfferListFilterContext();

  const { product } = useProductContext();
  const history = useHistory();
  const userProductAlerts: {
    productId: number;
    alertId: number;
  }[] =
    useSelector((state: AppState) => state.areas.sidebar.alerts.products) ?? [];

  const filtersToggled = filtersToggledOnByUser();

  const [modalOpen, setModalOpen] = useState(false);
  const [prompt, setPrompt] = useState<PromptMessageEnum | null>();
  const [message, setMessage] = useState<InfoMessageEnum | null>(null);
  const [allShownOffers] = useState([
    ...offers.mainList,
    ...offers.secondaryList,
  ]);

  const usedSelected =
    filtersToggled.filter(filter => filter.type === FilterType.CONDITION)
      .length > 0;

  const hasOffers =
    offers.mainList.filter(offer => offer.stock?.status === 'in_stock').length >
    0;

  const hasAnyOffers = allShownOffers.length > 0;
  const hasAnyInStock =
    allOffers.filter(offer => offer.stock?.status === 'in_stock').length > 0;

  const hasFeaturedOffers =
    hasAnyInStock &&
    offers.mainList.filter(offer => offer.store?.featured).length > 0;

  const anyOffersInList = offers.mainList.length > 0;

  const inclShippingSelected =
    filtersToggled.filter(
      filter => filter.type === FilterType.PRICES_INC_SHIPPING
    ).length > 0;

  const inStockSelected =
    filtersToggled.filter(filter => filter.type === FilterType.IN_STOCK)
      .length > 0;

  useEffect(() => {
    setupPrompt();
  }, []);

  useEffect(() => {
    setMessage(null);
    setupMessage();
  }, [offers]);

  const submitAlert = (
    event: React.FormEvent,
    email?: string,
    onError?: (error?: Errors) => void
  ) => {
    event.preventDefault();
    if (!isAuthenticated && email && !isValidEmail(email) && onError)
      return onError(Errors.ERROR_EMAIL_INVALID);

    const userAlert = userProductAlerts.find(
      alert => alert.productId === product.id
    );

    const stockStatusAlert = {
      alertId: userAlert?.alertId,
      email: !isAuthenticated ? email : undefined,
      productId: product.id,
      type: AlertTypes.IN_STOCK,
    };

    return dispatch(createAlertRequest(stockStatusAlert));
  };

  const handleReset = () => {
    resetFilters();
  };

  const handleResetShipping = () => {
    toggleFilter(FilterType.PRICES_INC_SHIPPING);
  };

  const getCategory = () => {
    const category: any = {
      ...product.category?.path.slice(-1)[0],
    };
    category.name = category?.name || '';
    category.pathName = category?.pathName || '';
    return category;
  };

  const resetFilterLink = (
    // eslint-disable-next-line
    <a onClick={handleReset}>{t('filter', 'button.clear-filters')}</a>
  );

  const resetShippingLink = (
    // eslint-disable-next-line
    <a onClick={handleResetShipping}>
      {t('filter', 'empty-message.show-without-delivery-cost')}
    </a>
  );

  const setupPrompt = () => {
    let newPrompt: PromptMessageEnum | null = null;

    if (!hasAnyOffers) {
      newPrompt = PromptMessageEnum.noOffers;
    } else if (!hasAnyInStock) {
      newPrompt = PromptMessageEnum.outOfStock;
    } else if (!hasFeaturedOffers) {
      newPrompt = PromptMessageEnum.noFeaturedSellsProduct;
    }

    setPrompt(newPrompt);
  };

  const setupMessage = () => {
    let newMessage: InfoMessageEnum | null = null;

    if (!anyOffersInList && filtersToggled.length > 1) {
      newMessage = InfoMessageEnum.noFiltersMatch;
    } else if (inclShippingSelected && !anyOffersInList) {
      newMessage = InfoMessageEnum.noInfoOnDelivery;
    } else if (!hasOffers && filtersToggled.length > 0 && inStockSelected) {
      if (prompt && usedSelected) {
        newMessage = InfoMessageEnum.outOfStock;
      }
    }

    setMessage(newMessage);
  };

  //  NoStockStatus prompt should only be shown for 'new' products.
  //  That's because the prompt has an incentive to create a stock alert,
  //  and the stock alert function doesn't send notifications for 'used' products that is out of stock.
  //  Don't show it either if there is any filter activated, because it has it's own empty offer list message.
  const renderNothingInStockMessage = () => {
    if (!prompt) {
      return null;
    }

    const category = getCategory();

    let promptMessage: PromptMessageType = {
      message: '',
      buttonText: '',
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      buttonOnClick: () => {},
      icon: null,
      iconPosition: 'left',
    };

    switch (prompt) {
      case PromptMessageEnum.noOffers:
        promptMessage = {
          message: t('filter', 'empty-message.no-store-sells-product'),
          buttonText: t('filter', 'empty-message.see-other-categories', {
            category: category.name,
          }),
          buttonOnClick: () => history.push(category.pathName),
          icon: <IconChevronRight />,
          iconPosition: 'right',
        };
        break;

      case PromptMessageEnum.outOfStock:
        promptMessage = {
          message: t('filter', 'empty-message.no-product-in-store'),
          buttonText: t('filter', 'empty-message.notify-me-button'),
          buttonOnClick: () => setModalOpen(true),
          icon: <IconNotificationOutlined />,
          iconPosition: 'left',
        };
        break;

      case PromptMessageEnum.noFeaturedSellsProduct:
        promptMessage = {
          message: t('filter', 'empty-message.no-featured-store-sells-product'),
          buttonText: t('filter', 'empty-message.see-other-categories', {
            category: category.name,
          }),
          buttonOnClick: () => history.push(category.pathName),
          icon: <IconChevronRight />,
          iconPosition: 'right',
        };
        break;

      default:
        return null;
    }

    if (prompt === PromptMessageEnum.outOfStock && usedSelected) {
      return null;
    }

    return (
      <>
        <StockStatusAlertModal
          closeModal={() => setModalOpen(false)}
          isOpen={modalOpen}
          onSubmit={submitAlert}
        />
        <PromptMessage message={promptMessage} />
      </>
    );
  };

  const renderMessage = () => {
    if (!message) {
      return null;
    }

    let infoMessage: InfoMessageType = {
      text: '',
      link: null,
    };

    switch (message) {
      case InfoMessageEnum.noFiltersMatch:
        infoMessage = {
          text: t('filter', 'empty-message.no-filter-match'),
          link: resetFilterLink,
        };
        break;
      case InfoMessageEnum.noInfoOnDelivery:
        infoMessage = {
          text: t('filter', 'empty-message.no-info-on-delivery-cost'),
          link: resetShippingLink,
        };
        break;
      case InfoMessageEnum.outOfStock:
        infoMessage = {
          text: t('filter', 'empty-message.no-product-in-store'),
          link: resetShippingLink,
        };
        break;

      default:
        return null;
    }

    return <InfoMessage message={infoMessage} />;
  };

  return (
    <>
      {renderMessage()}
      {renderNothingInStockMessage()}
    </>
  );
};

export default OfferListEmptyMessage;
